import { Form, Formik } from 'formik';
import { memo, useCallback, useState } from 'react';
import { injectStripe, type ReactStripeElements } from 'react-stripe-elements';
import { useMountedState } from 'react-use';

import { ChevronLeft } from '@air/next-icons';
import { Button } from '@air/primitive-button';

import { PaymentFields } from '~/components/Workspace/Checkout/PaymentFields/PaymentFields';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useUpdatePaymentMethodMutation } from '~/swr-hooks/subscriptions/useUpdatePaymentMethodMutation';

const SUCCESS_MESSAGE_TIMEOUT = 2000;

export type PaymentUpdateFormProps = {
  onBack: () => void;
} & ReactStripeElements.InjectedStripeProps;

const PaymentUpdateFormComponent = memo(({ stripe, onBack }: PaymentUpdateFormProps) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const [showSuccess, setShowSuccess] = useState(false);
  const isMounted = useMountedState();
  const updatePaymentMethodMutation = useUpdatePaymentMethodMutation();

  const updatePayment = useCallback(() => {
    const workspaceId = currentWorkspace?.id;

    if (!workspaceId) {
      return;
    }

    updatePaymentMethodMutation.mutate(
      {
        stripe,
        workspaceId,
      },
      {
        onSuccess: () => {
          // Show success state
          setShowSuccess(true);

          // Show a screen confirming the payment was updated then
          // return to the original screen, using setTimeout to achieve that UX
          setTimeout(() => {
            if (isMounted()) {
              onBack();
            }
          }, SUCCESS_MESSAGE_TIMEOUT);
        },
      },
    );
  }, [currentWorkspace?.id, isMounted, onBack, stripe, updatePaymentMethodMutation]);

  const handleSubmit = useCallback(() => {
    updatePayment();
  }, [updatePayment]);

  if (!stripe) {
    return (
      <div className="py-12 text-center">
        <div className="text-14 text-grey-11">Loading payment system...</div>
      </div>
    );
  }

  // Show success message if payment was updated
  if (showSuccess) {
    return (
      <div className="py-8 text-center">
        <div className="mb-4 text-48">✓</div>
        <h2 className="mb-2 font-semibold text-20 text-grey-12">Payment Updated Successfully</h2>
        <p className="text-14 text-grey-11">Your payment information has been updated.</p>
      </div>
    );
  }

  return (
    <Formik initialValues={{}} onSubmit={handleSubmit}>
      {({ isSubmitting }) => {
        return (
          <Form>
            <Button
              onClick={onBack}
              appearance="ghost"
              color="grey"
              size="small"
              className="mb-4 flex items-center"
              disabled={isSubmitting}
            >
              <ChevronLeft className="mr-1 size-4" />
              Back
            </Button>

            <h2 className="mb-4 font-semibold text-20 text-grey-12">Update payment method</h2>

            <PaymentFields disabled={isSubmitting} />

            {updatePaymentMethodMutation.error && (
              <div className="mt-4 text-14 text-red-9">{updatePaymentMethodMutation.error.message}</div>
            )}

            <Button
              appearance="filled"
              className="mt-4"
              color="blue"
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting}
              size="large"
            >
              Confirm
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
});

PaymentUpdateFormComponent.displayName = 'PaymentUpdateFormComponent';

export const PaymentUpdateForm = injectStripe(PaymentUpdateFormComponent);
